import { useState, useCallback, useRef } from 'react';
import { message } from 'antd';
import axios from 'axios';
import { documentApiService } from '../services/documentApi';
import { SUPPORTED_PREVIEW_TYPES } from '../constants/fileTypes';

export const useDocumentPreview = () => {
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [isPreviewModalVisible, setIsPreviewModalVisible] = useState(false);
  const [pdfFile, setPdfFile] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isStreaming, setIsStreaming] = useState(false);
  const isLoadingPreview = useRef(false);

  const handlePreview = useCallback(async (document) => {
    // Prevent multiple simultaneous calls
    if (isLoadingPreview.current) {
      return;
    }

    const fileExtension = document.name.substring(document.name.lastIndexOf('.')).toLowerCase();

    if (!SUPPORTED_PREVIEW_TYPES.includes(fileExtension)) {
      message.info('Preview not available for this file type.');
      return;
    }

    try {
      isLoadingPreview.current = true;
      let fileUrl = await documentApiService.getPresignedUrl(document.name);

      if (fileExtension === '.docx' || fileExtension === '.pptx') {
        try {
          fileUrl = await documentApiService.convertToPdf(document.name);
        } catch (conversionError) {
          console.error('Error converting file to PDF:', conversionError);
          message.error('Unable to convert file to PDF for preview. Please download the file to view it.');
          return;
        }
      }

      const pdfResponse = await axios.get(fileUrl, { responseType: 'arraybuffer' });
      setPdfFile(pdfResponse.data);
      setSelectedDocument(document);
      setIsPreviewModalVisible(true);
      setChatMessages([]);
    } catch (error) {
      console.error('Error fetching PDF:', error);
      message.error('Error previewing file. Please try again.');
    } finally {
      isLoadingPreview.current = false;
    }
  }, []);

  const handleSendMessage = useCallback(async (message) => {
    if (!message.trim() || isStreaming) return;

    // Add user message
    setChatMessages((prevMessages) => [
      ...prevMessages,
      { type: 'user', content: message },
    ]);

    // Add placeholder for AI response
    const aiMessageIndex = chatMessages.length + 1;
    setChatMessages((prevMessages) => [
      ...prevMessages,
      { type: 'ai', content: '', pages: [], isStreaming: true },
    ]);

    setIsStreaming(true);

    try {
      // Build conversation history for the API
      const conversationHistory = chatMessages.map(msg => ({
        role: msg.type === 'user' ? 'user' : 'assistant',
        content: msg.content
      }));
      
      let streamedContent = '';
      let pages = [];
      
      await documentApiService.queryChatbotStream(
        selectedDocument.name, 
        message, 
        conversationHistory,
        (data) => {
          if (data.type === 'start') {
            console.log('Stream started');
          } else if (data.type === 'content') {
            // Accumulate content
            streamedContent += data.content;
            
            // Update the AI message with accumulated content
            setChatMessages((prevMessages) => {
              const newMessages = [...prevMessages];
              newMessages[aiMessageIndex] = {
                type: 'ai',
                content: streamedContent,
                pages: pages,
                isStreaming: true
              };
              return newMessages;
            });
          } else if (data.type === 'complete') {
            // Stream completed
            pages = data.pages || [];
            
            // Final update with complete data
            setChatMessages((prevMessages) => {
              const newMessages = [...prevMessages];
              newMessages[aiMessageIndex] = {
                type: 'ai',
                content: streamedContent,
                pages: pages,
                isStreaming: false
              };
              return newMessages;
            });
            
            // Update current page if pages are found
            if (pages.length > 0) {
              setCurrentPage(pages[0]);
            }
          } else if (data.type === 'error') {
            throw new Error(data.error);
          }
        }
      );
    } catch (error) {
      console.error('queryChatbotStream error:', error);
      setChatMessages((prevMessages) => {
        const newMessages = [...prevMessages];
        newMessages[aiMessageIndex] = {
          type: 'ai',
          content: 'Sorry, I encountered an error. Please try again.',
          isStreaming: false
        };
        return newMessages;
      });
    } finally {
      setIsStreaming(false);
    }
  }, [selectedDocument, chatMessages, isStreaming]);

  const handleClearChat = useCallback(() => {
    setChatMessages([]);
  }, []);

  const handlePreviewModalCancel = useCallback(() => {
    setIsPreviewModalVisible(false);
    setPdfFile(null);
    setSelectedDocument(null);
    setChatMessages([]);
    setCurrentPage(1);
    setIsStreaming(false);
  }, []);

  return {
    selectedDocument,
    isPreviewModalVisible,
    pdfFile,
    chatMessages,
    currentPage,
    isStreaming,
    setCurrentPage,
    handlePreview,
    handleSendMessage,
    handleClearChat,
    handlePreviewModalCancel,
  };
};